import * as React from 'react';
import {cva, type VariantProps} from 'class-variance-authority';
import {useAutoResizeTextarea} from '../hooks/use-auto-resize-textarea';
import {cn} from '../lib/utils';

const textareaVariants = cva(
  cn(
    'flex min-h-[60px] w-full rounded-lg border border-input px-3 py-2',
    'text-sm',
    'placeholder:text-muted-foreground',
    'focus-visible:outline-hidden focus-visible:border-ring-muted focus-visible:ring-2 focus-visible:ring-border',
    'disabled:cursor-not-allowed disabled:opacity-50',
  ),
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        filled: 'bg-background',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textareaVariants> {
  autoResize?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({className, autoResize = false, variant, value, ...props}, ref) => {
    const {textAreaRef} = useAutoResizeTextarea(ref, autoResize, value);
    return (
      <textarea
        className={cn(
          textareaVariants({variant}),
          autoResize && 'resize-none',
          className,
        )}
        ref={textAreaRef}
        value={value}
        {...props}
      />
    );
  },
);
Textarea.displayName = 'Textarea';

export {Textarea, textareaVariants};
